import { ApplicationConfig, inject, isDevMode, provideAppInitializer, provideBrowserGlobalErrorListeners } from '@angular/core';
import { provideHttpClient } from '@angular/common/http';
import { provideRouter, withInMemoryScrolling, withViewTransitions } from '@angular/router';
import { provideOptimus } from '@openng/optimus-ui/config';
import { definePreset } from '@openng/optimus-ui-themes';
import Aura from '@openng/optimus-ui-themes/aura';
import { provideTransloco, provideTranslocoScope } from '@jsverse/transloco';

import { routes } from './app.routes';
import { provideServiceWorker } from '@angular/service-worker';
import { AppTranslocoLoader } from './core/i18n/transloco-loader';
import { LanguageService } from './core/i18n/language.service';
import { onRouteViewTransition } from './shared/animations/route-transition';

// Aura's stock primary is emerald; remap it to our blue brand ramp so Optimus UI-native controls
// (switch, slider, checkbox, focus rings, dropdown highlight) match the design tokens.
const BlueAura = definePreset(Aura, {
  semantic: {
    primary: {
      50: '#eff6ff',
      100: '#dbeafe',
      200: '#bfdbfe',
      300: '#93c5fd',
      400: '#60a5fa',
      500: '#3b82f6',
      600: '#2563eb',
      700: '#1d4ed8',
      800: '#1e40af',
      900: '#1e3a8a',
      950: '#172554',
    },
  },
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideHttpClient(),
    provideTransloco({
      config: {
        availableLangs: ['en', 'de', 'fr', 'es', 'it'],
        defaultLang: 'en',
        fallbackLang: 'en',
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
        missingHandler: {
          allowEmpty: false,
          logMissingKey: !isDevMode() ? false : true,
          useFallbackTranslation: true,
        },
      },
      loader: AppTranslocoLoader,
    }),
    provideTranslocoScope('core'),
    provideAppInitializer(() => inject(LanguageService).initialize()),
    provideRouter(
      routes,
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'enabled' }),
      withViewTransitions({ skipInitialTransition: true, onViewTransitionCreated: onRouteViewTransition }),
    ),
    provideOptimus({
      theme: {
        preset: BlueAura,
        options: { darkModeSelector: '.app-dark' },
      },
    }),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
};
